<template>
  <v-dialog
    :value="value"
    persistent
    max-width="640"
    @input="onDialogInput"
  >
    <v-card v-if="bdc">
      <v-card-title class="headline" style="color: #e2007a">
        <v-icon left color="#e2007a">mdi-file-sign</v-icon>
        Confirmer la correction et déclencher la re-signature
      </v-card-title>

      <v-card-text style="font-size: 13px">
        <div style="margin-bottom: 12px">
          <strong>BDC :</strong> N° {{ bdc.numeroEditeur }}
          <span v-if="bdc.resident">
            - {{ bdc.resident.prénom }} {{ bdc.resident.nom }}
          </span>
        </div>

        <v-divider class="my-2"></v-divider>

        <!-- Bannière transformation libéral ↔ MSP, mise en avant car action lourde
             (recalcul tarifaire serveur + changement de mspID + reset/init aides). -->
        <v-alert
          v-if="diff && diff.partyTransform"
          type="info"
          outlined
          dense
          class="mb-3"
          icon="mdi-swap-horizontal"
        >
          <strong>Type de structure :</strong> {{ partyTransformLabel }}
          <div style="font-size: 11px; color: #555; margin-top: 4px">
            Le typePrestation et le montantSegurHT seront recalculés par le serveur
            selon la grille tarifaire du partenaire.
          </div>
        </v-alert>

        <!-- Récap diff par section -->
        <div v-if="!hasChanges" style="color: #888; font-style: italic">
          Aucun champ modifié.
        </div>

        <div
          v-for="section in sections"
          :key="section.key"
          v-show="hasSectionDiff(section.key)"
        >
          <div class="diff-section-title">
            <v-icon small left :color="section.color">{{
              section.icon
            }}</v-icon>
            {{ section.label }}
          </div>
          <table class="diff-table">
            <thead>
              <tr>
                <th>Champ</th>
                <th>Avant</th>
                <th>Après</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="line in sectionDiffLines(section.key)"
                :key="section.key + '-' + line.field"
              >
                <td>
                  <strong>{{ line.label }}</strong>
                </td>
                <td class="diff-old">{{ formatVal(line.before) }}</td>
                <td class="diff-new">{{ formatVal(line.after) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <v-divider class="my-3"></v-divider>
        <v-textarea
          :value="resignReason"
          label="Motif de la correction (optionnel)"
          placeholder="Ex : erreur sur le FINESS, montant incorrect, email mal saisi..."
          outlined
          rows="2"
          counter="500"
          :disabled="loading"
          :rules="motifRules"
          @input="$emit('update:resignReason', $event)"
        ></v-textarea>
        <p style="font-size: 11px; color: #888; margin-top: -4px">
          Apparaîtra dans le mail au médecin et dans la WebView SEGUR.
          Laisser vide efface l'éventuel motif précédent.
        </p>

        <v-alert
          type="warning"
          outlined
          dense
          class="mt-3"
          icon="mdi-email-alert-outline"
        >
          Le médecin sera notifié par email et devra rouvrir son logiciel
          métier pour re-signer le BDC. Cette action est tracée dans l'audit log.
        </v-alert>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text :disabled="loading" @click="onCancel">Annuler</v-btn>
        <v-btn
          color="#e2007a"
          dark
          :disabled="!hasChanges || loading"
          :loading="loading"
          @click="$emit('confirm')"
        >
          <v-icon left small>mdi-check</v-icon>
          Confirmer (déclenchera la re-signature)
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// Confirmation avant PATCH : affiche le diff par section et le rappel que
// la re-signature médecin sera déclenchée et tracée dans l'audit.

const FIELD_LABELS = {
  orderForm: {
    montantSegurHT: "Montant SEGUR HT",
    typePrestation: "Type de prestation",
    dateSignatureCommande: "Date signature commande",
    codePostalMedecin: "Code postal médecin (TVA)",
    aidesPercuesBool: "Aides perçues",
    aidesDeMinimis: "Tableau des aides",
  },
  resident: {
    email: "Email",
    phone: "Téléphone",
    address: "Adresse cabinet",
    zip: "Code postal cabinet",
    city: "Ville",
  },
  msp: {
    FINESS: "FINESS",
    FINESSGEO: "FINESS géographique",
    raisonSociale: "Raison sociale",
  },
};

const SECTIONS = [
  {
    key: "orderForm",
    label: "Bon de commande",
    icon: "mdi-file-document-edit-outline",
    color: "#e2007a",
  },
  {
    key: "resident",
    label: "Médecin",
    icon: "mdi-account-edit-outline",
    color: "#2196f3",
  },
  {
    key: "msp",
    label: "Structure",
    icon: "mdi-office-building-outline",
    color: "#9c27b0",
  },
];

export default {
  name: "ConfirmResignModal",

  props: {
    value: { type: Boolean, default: false },
    bdc: { type: Object, default: null },
    // diff = { orderForm: {...}, resident: {...}, msp: {...} } - sections optionnelles
    diff: { type: Object, default: () => ({}) },
    resignReason: { type: String, default: "" },
    loading: { type: Boolean, default: false },
  },

  data() {
    return {
      motifRules: [
        (v) => !v || v.length <= 500 || "Motif limité à 500 caractères",
      ],
    };
  },

  computed: {
    sections() {
      return SECTIONS;
    },

    hasChanges() {
      return (
        Object.keys(this.diff || {}).length > 0 &&
        Object.values(this.diff).some((s) => s && Object.keys(s).length > 0)
      );
    },

    // Libellé bannière transformation. Affiche FINESS pour la cible MSP
    // (élément de contrôle visuel important pour l'opérateur avant validation).
    partyTransformLabel() {
      const t = this.diff && this.diff.partyTransform;
      if (!t) return "";
      if (t.target === "msp") {
        return `libéral vers MSP (FINESS ${t.finess})`;
      }
      return "MSP vers libéral";
    },
  },

  methods: {
    hasSectionDiff(sectionKey) {
      const s = this.diff && this.diff[sectionKey];
      return !!(s && Object.keys(s).length > 0);
    },

    // Construit { field, label, before, after } pour chaque champ modifié.
    sectionDiffLines(sectionKey) {
      const sectionDiff = (this.diff && this.diff[sectionKey]) || {};
      const labels = FIELD_LABELS[sectionKey] || {};
      const beforeSource = this.beforeSource(sectionKey);
      return Object.keys(sectionDiff).map((field) => ({
        field,
        label: labels[field] || field,
        before: beforeSource[field],
        after: sectionDiff[field],
      }));
    },

    // Valeurs "avant" : champs orderForm directement sur bdc, resident/msp sur bdc.{section}.
    beforeSource(sectionKey) {
      if (!this.bdc) return {};
      if (sectionKey === "orderForm") return this.bdc;
      if (sectionKey === "resident") return this.bdc.resident || {};
      if (sectionKey === "msp") return this.bdc.msp || {};
      return {};
    },

    formatVal(v) {
      if (v === null || v === undefined || v === "") return "(vide)";
      if (typeof v === "boolean") return v ? "oui" : "non";
      // Tableau d'aides de minimis : rendu lisible ligne par ligne.
      if (Array.isArray(v)) {
        if (v.length === 0) return "aucune ligne";
        return v
          .map((aide) => {
            const date = aide?.dateAttribution || "?";
            const montant =
              aide?.montantAides !== undefined && aide?.montantAides !== null
                ? `${aide.montantAides} €`
                : "?";
            return `${date} : ${montant}`;
          })
          .join(" ; ");
      }
      if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) {
        const d = new Date(v);
        if (!Number.isNaN(d.getTime())) {
          return (
            ("0" + d.getDate()).slice(-2) +
            "/" +
            ("0" + (d.getMonth() + 1)).slice(-2) +
            "/" +
            d.getFullYear()
          );
        }
      }
      return String(v);
    },

    onCancel() {
      if (this.loading) return;
      this.$emit("input", false);
      this.$emit("cancel");
    },

    // Bloque la fermeture pendant le PATCH (ESC, persistent, etc.).
    onDialogInput(open) {
      if (this.loading && !open) return;
      this.$emit("input", open);
    },
  },
};
</script>

<style scoped>
.diff-section-title {
  font-size: 13px;
  font-weight: bold;
  margin-top: 8px;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
}

.diff-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  margin-bottom: 8px;
}

.diff-table th,
.diff-table td {
  text-align: left;
  padding: 4px 8px;
  border-bottom: 1px solid #eee;
  vertical-align: top;
}

.diff-table th {
  background: #f5f5f5;
  font-weight: 600;
}

.diff-old {
  color: #c62828;
  text-decoration: line-through;
}

.diff-new {
  color: #2e7d32;
  font-weight: 600;
}
</style>
